import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, getStoredUser } from '../lib/api';
import { Icon } from '../components/icons';
import { Badge, PageHeader, SectionCard, Spinner } from '../components/ui';
import { fmtMoney, fmtNumber, fmtRelative } from '../lib/format';

interface RoomItem {
  id: string;
  name: string;
  maxPlayers: number;
  gameType: string;
  isPrivate: boolean;
  rakePct: number;
  totalRakeCollected: number;
  buyIn: number;
  iconId: string;
  createdAt: number;
  ownerId: string;
  ownerNickname: string | null;
  override: { notes: string | null; tags: string[]; pinned: boolean } | null;
}

interface PeekGrant {
  id: string;
  grantedBy: string;
  targetUserId: string;
  roomId: string | null;
  reason: string | null;
  grantedAt: number;
  expiresAt: number | null;
  revokedAt: number | null;
}

export function RoomDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [rooms, setRooms] = useState<RoomItem[] | null>(null);
  const [grants, setGrants] = useState<PeekGrant[]>([]);
  const [notes, setNotes] = useState('');
  const [tags, setTags] = useState('');
  const [pinned, setPinned] = useState(false);
  const [peekFor, setPeekFor] = useState('');
  const [peekHand, setPeekHand] = useState<{ id: string; suit: string; rank: string }[] | null>(null);
  const [peekErr, setPeekErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get<{ items: RoomItem[] }>('/api/crm/rooms').then((r) => {
      setRooms(r.items);
      const found = r.items.find((x) => x.id === id);
      if (found?.override) {
        setNotes(found.override.notes ?? '');
        setTags((found.override.tags ?? []).join(', '));
        setPinned(found.override.pinned);
      }
    });
    api.get<{ items: PeekGrant[] }>('/api/crm/peek/grants').then((r) => setGrants(r.items));
  }, [id]);

  if (!rooms) {
    return <div className="flex items-center gap-2 text-stone-400 text-sm"><Spinner /> Загрузка…</div>;
  }
  const room = rooms.find((r) => r.id === id);
  if (!room) {
    return <div className="text-sm text-stone-400">Комната не найдена. <button className="underline" onClick={() => navigate('/rooms')}>Назад</button></div>;
  }

  const saveOverride = async () => {
    setBusy(true);
    try {
      await api.patch(`/api/crm/rooms/${room.id}/override`, {
        notes: notes || null,
        tags: tags.split(',').map((s) => s.trim()).filter(Boolean),
        pinned
      });
      const r = await api.get<{ items: RoomItem[] }>('/api/crm/rooms');
      setRooms(r.items);
    } finally { setBusy(false); }
  };

  const forceEnd = async () => {
    if (!confirm('Досрочно завершить партию в этой комнате?')) return;
    setBusy(true);
    try {
      await api.post(`/api/crm/rooms/${room.id}/force-end`);
      alert('Партия завершена');
    } finally { setBusy(false); }
  };

  // === Снятие кассы (rake withdraw) ===
  const me = getStoredUser();
  const canWithdraw = !!me?.permissions.includes('rake:withdraw');
  const withdrawRake = async () => {
    if (room.totalRakeCollected <= 0) {
      alert('Касса пуста — снимать нечего.');
      return;
    }
    const msg = `Снять кассу с комнаты «${room.name}»?\n\nСумма: ${fmtMoney(room.totalRakeCollected)} фишек.\n\nЗапись появится в истории раунда и в crm_audit_log.\nКасса комнаты будет сброшена в 0 и начнёт копиться заново.`;
    if (!confirm(msg)) return;
    setBusy(true);
    try {
      const r = await api.post<{ amount: number; withdrawnAt: number }>(`/api/crm/rooms/${room.id}/rake/withdraw`);
      if (r.amount <= 0) {
        alert('Касса пуста — снимать нечего.');
      } else {
        alert(`Снято ${fmtMoney(r.amount)} фишек. Записано в историю.`);
        // Refresh rooms list (totalRakeCollected изменился)
        const refreshed = await api.get<{ items: RoomItem[] }>('/api/crm/rooms');
        setRooms(refreshed.items);
      }
    } catch (e: any) {
      alert(`Ошибка: ${e?.error || e?.message || 'неизвестно'}`);
    } finally { setBusy(false); }
  };

  const doPeek = async () => {
    setPeekErr(null); setPeekHand(null);
    if (!peekFor) { setPeekErr('Введите userId игрока'); return; }
    setBusy(true);
    try {
      const r = await api.post<{ hand: { id: string; suit: string; rank: string }[] }>(`/api/crm/rooms/${room.id}/peek`, { userId: peekFor });
      setPeekHand(r.hand);
    } catch (e: any) {
      setPeekErr(e?.error === 'NOT_FOUND' ? 'Игрок не в комнате или не в burkozel' : 'Ошибка');
    } finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader
        title={room.name}
        subtitle={`ID ${room.id}`}
        actions={
          <>
            <button className="btn btn-secondary" onClick={() => navigate('/rooms')}><Icon.Chevron size={14} className="rotate-180" />К списку</button>
            <button className="btn btn-danger" onClick={forceEnd} disabled={busy}><Icon.Power size={14} />Досрочно закончить</button>
          </>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <div className="stat-card">
          <div className="label">Владелец</div>
          <div className="value text-base">{room.ownerNickname ?? '—'}</div>
        </div>
        <div className="stat-card">
          <div className="label">Тип</div>
          <div className="value text-base">{room.gameType}</div>
        </div>
        <div className="stat-card">
          <div className="label">Банк / Buy-in</div>
          <div className="value text-base">{fmtMoney(room.buyIn)}</div>
        </div>
        <div className="stat-card">
          <div className="label flex items-center justify-between">
            <span>Заработано</span>
            {canWithdraw && room.totalRakeCollected > 0 && (
              <button
                className="btn btn-gold px-2 py-0.5 text-[11px] normal-case tracking-normal"
                onClick={withdrawRake}
                disabled={busy}
                title="Снять кассу — обнулить накопленный rake и записать в историю"
              >
                <Icon.Cash size={12} /> Снять кассу
              </button>
            )}
          </div>
          <div className="value text-base text-emerald-300">{fmtMoney(room.totalRakeCollected)}</div>
          {canWithdraw && room.totalRakeCollected === 0 && (
            <div className="delta">касса пуста</div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <SectionCard title="Заметки CRM">
          <textarea
            className="input min-h-[120px]"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Служебные заметки (видны только сотрудникам CRM)…"
          />
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-[1fr,auto] gap-2 items-end">
            <div>
              <label className="label">Теги (через запятую)</label>
              <input className="input mt-1" value={tags} onChange={(e) => setTags(e.target.value)} placeholder="vip, high-stakes, suspicious" />
            </div>
            <label className="flex items-center gap-2 text-sm text-stone-300">
              <input type="checkbox" className="accent-amber-400" checked={pinned} onChange={(e) => setPinned(e.target.checked)} />
              Закрепить
            </label>
          </div>
          <button className="btn btn-gold mt-3" disabled={busy} onClick={saveOverride}>
            <Icon.Check size={14} /> Сохранить
          </button>
        </SectionCard>

        <SectionCard title="Пик руки игрока">
          <p className="text-xs text-stone-400 mb-2">Введите userId игрока — увидите его закрытую руку. Доступно только при наличии права <code>rooms:peek</code>.</p>
          <div className="flex flex-col sm:flex-row gap-2">
            <input className="input flex-1" value={peekFor} onChange={(e) => setPeekFor(e.target.value)} placeholder="UUID игрока" />
            <button className="btn btn-secondary" onClick={doPeek} disabled={busy}><Icon.Eye size={14} />Смотреть</button>
          </div>
          {peekErr && <div className="text-xs text-red-300 mt-2">{peekErr}</div>}
          {peekHand && (
            <div className="mt-3">
              <div className="text-xs text-stone-400 mb-1">Рука ({peekHand.length})</div>
              <div className="flex flex-wrap gap-2">
                {peekHand.length === 0 ? <span className="text-stone-500 text-sm">Пусто</span> : peekHand.map((c) => (
                  <CardChip key={c.id} rank={c.rank} suit={c.suit} />
                ))}
              </div>
            </div>
          )}
        </SectionCard>
      </div>
    </div>
  );
}

function CardChip({ rank, suit }: { rank: string; suit: string }) {
  const isRed = suit === 'H' || suit === 'D';
  const suitGlyph = suit === 'S' ? '♠' : suit === 'H' ? '♥' : suit === 'D' ? '♦' : '♣';
  return (
    <div className={`px-2.5 py-2 rounded-lg bg-white/95 text-stone-900 ring-1 ring-stone-200 font-display font-bold text-sm flex flex-col items-center min-w-[44px] ${isRed ? 'text-red-600' : ''}`}>
      <span>{rank}</span>
      <span className="text-base leading-none">{suitGlyph}</span>
    </div>
  );
}
